import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { EmailBuilderDataService } from '@galaxy/email-ui/email-builder';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { And, RuleInterface, StringEquals } from '@vendasta/automata';
import { AppNamespace, EmailBuilderTemplate, OwnerType } from '@vendasta/email-builder';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInputModule, SelectInputOption } from '@vendasta/galaxy/input';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { ObservableWorkStateMap } from '@vendasta/rx-utils/work-state';
import { Observable, Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import { PreviewImageContainerComponent } from '../../../common/preview-image-container/preview-image-container.component';
import { EmailEventTriggerService } from '../email-event.service';

@Component({
  selector: 'automata-email-event-panel',
  imports: [
    CommonModule,
    GalaxyFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    GalaxyInputModule,
    TranslateModule,
    MatIconModule,
    MatSelectModule,
    PreviewImageContainerComponent,
    ReactiveFormsModule,
    GalaxyAlertModule,
    GalaxyPipesModule,
  ],
  templateUrl: './email-event-panel.component.html',
  styleUrls: ['./email-event-panel.component.scss'],
})
export class EmailEventTriggerPanelComponent extends CommonTriggerPanelComponent implements OnInit, OnDestroy {
  private readonly RULE_INDICES = RULE_INDICES;
  private readonly ANY_LINK_OPTION = {
    value: '<ANY>',
    label: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.EMAIL_EVENT.ANY'),
  };

  linkClickedControl: UntypedFormControl = new UntypedFormControl('');

  private readonly getTemplateState = new ObservableWorkStateMap<string, EmailBuilderTemplate>();

  template$: Observable<EmailBuilderTemplate>;
  loading$: Observable<boolean>;
  linkOptions$: Observable<SelectInputOption[]> = new Observable();

  template_app_namespace: string;
  template_owner_type: string;
  template_owner_id: string;
  template_id: string;

  private subscriptions: Subscription[] = [];

  constructor(
    protected readonly translateService: TranslateService,
    protected readonly emailEventTriggerService: EmailEventTriggerService,
    @Inject(EmailBuilderDataService) private readonly emailBuilderDataService: EmailBuilderDataService,
  ) {
    super();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = {
      ...JSON.parse(this.waitForEventData || '{}'),
      ...JSON.parse(this.step?.data || '{}'),
    };
    let savedLink = '';
    if (this.triggerChoices?.rules?.length) {
      const rules = And.fromRuleInterface(this.triggerChoices?.rules[0]);
      savedLink = rules?.extractValue()[this.RULE_INDICES.link_clicked];
    }

    this.template_id = jsonData.template_id;
    this.template_app_namespace = jsonData.template_app_namespace || AppNamespace.AUTOMATIONS;
    this.template_owner_type = jsonData.template_owner_type || 'OWNER_TYPE_PARTNER';
    this.template_owner_id = jsonData.template_owner_id || this.automation.namespace;

    this.getTemplateState.startWork(
      this.template_id,
      this.emailBuilderDataService.getTemplate$(
        OwnerType.OWNER_TYPE_PARTNER,
        this.automation.namespace,
        AppNamespace.AUTOMATIONS,
        this.template_id,
      ),
    );

    this.template$ = this.getTemplateState.getWorkResults$(this.template_id);
    this.loading$ = this.getTemplateState.isLoading$(this.template_id);

    this.linkOptions$ = this.template$.pipe(
      filter((t) => !!t),
      map((template) => template.templateHtml),
      map((html) => this.emailEventTriggerService.extractClickableLinkUrlsFromTemplateHtml(html)),
      map((links) =>
        links.map((link) => {
          return { value: link, label: link };
        }),
      ),
      map((links) => [this.ANY_LINK_OPTION, ...links]),
      startWith([]),
    );

    this.subscriptions.push(
      this.linkOptions$.subscribe((options) => {
        if (options.length && savedLink) {
          this.linkClickedControl.setValue(savedLink);
        } else {
          this.linkClickedControl.setValue(this.ANY_LINK_OPTION.value);
        }
      }),
    );

    this.savingEnabled$.next(true);
  }

  isFormValid(): boolean {
    return this.template_id && this.linkClickedControl.value && this.linkClickedControl.valid;
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    jsonData.template_id = this.template_id;
    jsonData.template_app_namespace = this.template_app_namespace;
    jsonData.template_owner_type = this.template_owner_type;
    jsonData.template_owner_id = this.template_owner_id;
    return jsonData;
  }

  getBaseRules(): RuleInterface[] {
    return [
      new And([
        new StringEquals('{.event_type}', 'clicked'),
        new StringEquals('{.url_clicked_normalized}', ''),
      ]).toRuleInterface(),
    ];
  }

  fillRuleValues(rules: RuleInterface[]): RuleInterface[] {
    const and = And.fromRuleInterface(rules[0]) as And;
    if (this.linkClickedControl.value === this.ANY_LINK_OPTION.value) {
      and.value = and.value.filter((a) => a.getVariablePath() !== '{.url_clicked_normalized}');
    } else if (and.listValuesByVariablePath('{.url_clicked_normalized}').length) {
      and.setValueByItsVariablePath('{.url_clicked_normalized}', this.linkClickedControl.value);
    } else {
      and.value.push(new StringEquals('{.url_clicked_normalized}', this.linkClickedControl.value));
    }
    return [and.toRuleInterface()];
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.linkClickedControl];
  }
}

enum RULE_INDICES {
  event_type = 0,
  link_clicked = 1,
}
